import { Language } from '../constants/language';
import { configurationService, AppConfiguration } from '../services/configuration-service';
import { CacheUtil } from './cache-util';
import { StorageUtil } from './storage-util';

/**
 * LanguageCacheManager - Centralized cache invalidation on language change
 *
 * This utility ensures that when the user switches language (AR ⇄ EN),
 * all cached entity data (ministries, events, schools, products) is invalidated
 * and fresh localized data is loaded.
 *
 * Features:
 * - Listens to configuration service language changes
 * - Invalidates both in-memory cache (CacheUtil) and persistent storage cache (StorageUtil)
 * - Provides defensive logging for all cache operations
 * - Emits events for components to trigger data re-fetch
 *
 * Usage:
 * - Initialize once at app startup via initializeLanguageCacheManager()
 * - Subscribe to onLanguageChange for components that need to react to language changes
 */

type LanguageChangeCallback = (newLanguage: Language, previousLanguage: Language) => void;

class LanguageCacheManagerClass {
  private isInitialized: boolean = false;
  private currentLanguage: Language | null = null;
  private unsubscribeFromConfig: (() => void) | null = null;
  private languageChangeListeners: LanguageChangeCallback[] = [];

  /**
   * Initialize the language cache manager
   * Should be called once at app startup
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }


    // Get initial language
    this.currentLanguage = configurationService.getLanguage();

    // Subscribe to configuration changes
    this.unsubscribeFromConfig = configurationService.subscribe(
      this.handleConfigurationChange.bind(this)
    );

    this.isInitialized = true;
  }

  /**
   * Cleanup and unsubscribe
   */
  destroy(): void {
    if (this.unsubscribeFromConfig) {
      this.unsubscribeFromConfig();
      this.unsubscribeFromConfig = null;
    }
    this.languageChangeListeners = [];
    this.isInitialized = false;
  }

  /**
   * Handle configuration changes from configurationService
   */
  private handleConfigurationChange(config: AppConfiguration): void {
    const newLanguage = config.language;
    const previousLanguage = this.currentLanguage;

    // Only react if language actually changed
    if (previousLanguage && newLanguage !== previousLanguage) {
      this.onLanguageChanged(newLanguage, previousLanguage);
    }

    this.currentLanguage = newLanguage;
  }

  /**
   * Called when language has changed
   * Invalidates all entity caches and notifies listeners
   */
  private onLanguageChanged(newLanguage: Language, previousLanguage: Language): void {
      // Step 1: Clear in-memory entity caches
      this.clearInMemoryCaches();

      // Step 2: Clear persistent storage entity caches
      this.clearPersistentCaches();

      // Step 3: Notify all listeners to trigger re-fetch
      this.notifyLanguageChangeListeners(newLanguage, previousLanguage);
  }

  /**
   * Clear all in-memory entity caches (CacheUtil)
   */
  private clearInMemoryCaches(): void {
    CacheUtil.clearAllEntityCaches();
  }

  /**
   * Clear persistent storage entity caches (StorageUtil)
   */
  private clearPersistentCaches(): void {

    const cacheKeys = [
      StorageUtil.KEYS.MINISTRIES_CACHE,
      StorageUtil.KEYS.EVENTS_CACHE,
      StorageUtil.KEYS.SCHOOLS_CACHE,
      StorageUtil.KEYS.PRODUCTS_CACHE
    ];

    cacheKeys.forEach(key => {
      const hadKey = StorageUtil.hasKey(key);
      if (hadKey) {
        StorageUtil.removeItem(key);
      }
    });

  }

  /**
   * Subscribe to language change events
   * Use this to trigger data re-fetch in components
   */
  onLanguageChange(callback: LanguageChangeCallback): () => void {
    this.languageChangeListeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.languageChangeListeners.indexOf(callback);
      if (index > -1) {
        this.languageChangeListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of language change
   */
  private notifyLanguageChangeListeners(newLanguage: Language, previousLanguage: Language): void {

    this.languageChangeListeners.forEach((callback, index) => {
      try {
        callback(newLanguage, previousLanguage);
      } catch (error) {
      }
    });
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): Language {
    return this.currentLanguage || configurationService.getLanguage();
  }

  /**
   * Check if manager is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Force clear all caches manually
   * Useful for debugging or when data refresh is needed
   */
  forceClearAllCaches(): void {
    this.clearInMemoryCaches();
    this.clearPersistentCaches();
  }
}

// Singleton instance
export const LanguageCacheManager = new LanguageCacheManagerClass();

/**
 * Initialize the language cache manager
 * Call this once at app startup
 */
export function initializeLanguageCacheManager(): void {
  LanguageCacheManager.initialize();
}

/**
 * Cleanup the language cache manager
 * Call this on app unmount if needed
 */
export function destroyLanguageCacheManager(): void {
  LanguageCacheManager.destroy();
}

export default LanguageCacheManager;
